import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { EvalFormat, evaluateTraining, TrainingEvalSummary } from '../../api/batch9Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Choose the format that matches your Trainee and Answer Key files.',
  'Upload the Trainee file and the Answer Key file (both .xlsx).',
  'Click Run Evaluation. The scored workbook (Trainee Validation + Billwise Marking sheets) downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function TrainingEvalPanel({ mission, onBack }: PanelProps) {
  const [format, setFormat] = useState<EvalFormat>('format1');
  const [trainee, setTrainee] = useState<File[]>([]);
  const [answer, setAnswer] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<TrainingEvalSummary>>();

  const handleSubmit = async () => {
    if (trainee.length === 0 || answer.length === 0) {
      fail('Upload both the Trainee file and the Answer Key.');
      return;
    }
    await run(() => evaluateTraining(format, trainee[0], answer[0]), 'Evaluation failed.');
  };

  const s = result?.summary?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="mb-5 flex gap-2">
        {([['format1', 'Format 1'], ['format2', 'Format 2 (Xero)']] as [EvalFormat, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFormat(key)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              format === key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <details className="mb-5 rounded-lg border border-border bg-muted px-4 py-3">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">
          {format === 'format1' ? 'Format 1' : 'Format 2 (Xero)'}: required columns and matching rules
        </summary>
        {format === 'format1' ? (
          <div className="mt-2 space-y-2 text-sm text-muted-foreground">
            <p>
              <strong>Required columns</strong> (both files): Client, Supplier, Invoice Number, Date, Due Date, Description, Net,
              VAT, Gross, Pg No of the Combined PDF, Account Code, Account Name, Vat Code.
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Invoices are matched by combined-PDF page number and Invoice Number (falling back to a unique Invoice Number, then a unique page number).</li>
              <li>If an Answer Key row contains "Duplicate Invoice" or "Purchase Order", that PDF page is excluded from scoring.</li>
              <li>A valid Answer Key page missing from the Trainee sheet scores 0.</li>
              <li>11 questions at 0.25 marks each: 2.75 marks maximum per valid invoice.</li>
              <li>Incorrect values are highlighted red-on-yellow in the Trainee Validation sheet.</li>
            </ul>
          </div>
        ) : (
          <div className="mt-2 space-y-2 text-sm text-muted-foreground">
            <p>
              <strong>Required columns</strong> (both files): Account Code, Account, Date, Source, Description, Invoice Number,
              Reference, Gross, VAT, Net, VAT Rate, VAT Rate Name. The header row is auto-detected in the first 150 rows.
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Rows are matched by Invoice Number only; each Answer Key row is matched at most once.</li>
              <li>Purchase Order / Duplicate Invoice rows are excluded from scoring.</li>
              <li>An Answer Key invoice with no matching Trainee row scores 0.</li>
              <li>10 questions at 0.25 marks each. Bills are sorted numerically by invoice number.</li>
            </ul>
          </div>
        )}
      </details>

      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop label="Trainee file" required accept={mission.accept} hint={mission.maxSizeLabel} accentColor={mission.color} files={trainee} onFiles={setTrainee} />
        <FileDrop label="Answer Key file" required accept={mission.accept} hint={mission.maxSizeLabel} accentColor={mission.color} files={answer} onFiles={setAnswer} />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Evaluating…' : 'Run Evaluation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Evaluating…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          {s && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ['Bills scored', s.bills_scored],
                  ['Total marks', `${s.total_marks} / ${s.maximum_marks}`],
                  ['Percentage', `${s.percentage}%`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                    <div className="text-lg font-bold text-foreground">{value}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
              {(s.excluded_pages > 0 || s.missing_pages > 0) && (
                <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
                  {s.excluded_pages} excluded page(s) (Duplicate/Purchase Order), {s.missing_pages} page(s) missing from the
                  Trainee file (scored 0).
                </div>
              )}
            </>
          )}
          {result.summary?.log && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">Processing log</summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{result.summary.log}</pre>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload both files above to enable evaluation.</InfoNotice>}
    </MissionShell>
  );
}

export default TrainingEvalPanel;
